import React from 'react';
import styled from 'styled-components';
import { FaShieldAlt, FaUserSecret, FaDatabase, FaEye } from 'react-icons/fa';

const Container = styled.div`
  min-height: 100vh;
  background-color: #000;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Content = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const Title = styled.h1`
  color: #fff;
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  color: #ccc;
  font-size: 18px;
  line-height: 1.6;
  max-width: 600px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const PrivacyContainer = styled.div`
  background-color: #111;
  border-radius: 12px;
  padding: 32px;
  border: 1px solid #333;
`;

const PrivacySection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PrivacySubtitle = styled.h3`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
`;

const PrivacyText = styled.div`
  color: #ccc;
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 16px;
`;

const PrivacyList = styled.ul`
  color: #ccc;
  font-size: 14px;
  line-height: 1.6;
  margin: 16px 0;
  padding-left: 20px;
`;

const PrivacyListItem = styled.li`
  margin-bottom: 8px;
`;

const DataTable = styled.div`
  margin: 20px 0;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: #222;
  border-radius: 8px;
  overflow: hidden;
`;

const TableHeader = styled.th`
  background-color: #333;
  color: #fff;
  padding: 12px;
  text-align: left;
  font-size: 14px;
  font-weight: 600;
`;

const TableCell = styled.td`
  color: #ccc;
  padding: 12px;
  font-size: 14px;
  border-top: 1px solid #333;
`;

const LastUpdated = styled.div`
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-top: 48px;
  padding-top: 24px;
  border-top: 1px solid #333;
`;

const PrivacyPage = () => {
  return (
    <Container>
      <Content>
        <Header>
          <Title>Privacy Policy</Title>
          <Subtitle>
            We are committed to protecting your privacy and ensuring the security of your personal information.
          </Subtitle>
        </Header>

        <PrivacyContainer>
          <PrivacySection>
            <PrivacySubtitle>1. Information We Collect</PrivacySubtitle>
            <PrivacyText>
              We collect information you provide directly to us, such as when you create an account, post content, 
              or communicate with other users. This may include:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>Account information (name, email, username, profile picture)</PrivacyListItem>
              <PrivacyListItem>Content you post (text, images, videos)</PrivacyListItem>
              <PrivacyListItem>Communications with other users</PrivacyListItem>
              <PrivacyListItem>Profile information and preferences</PrivacyListItem>
            </PrivacyList>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>2. How We Use Your Information</PrivacySubtitle>
            <PrivacyText>
              We use the information we collect to:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>Provide, maintain, and improve our services</PrivacyListItem>
              <PrivacyListItem>Process and complete transactions</PrivacyListItem>
              <PrivacyListItem>Send you technical notices and support messages</PrivacyListItem>
              <PrivacyListItem>Respond to your comments and questions</PrivacyListItem>
              <PrivacyListItem>Communicate with you about products, services, and events</PrivacyListItem>
              <PrivacyListItem>Monitor and analyze trends and usage</PrivacyListItem>
            </PrivacyList>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>3. Information Sharing</PrivacySubtitle>
            <PrivacyText>
              We do not sell, trade, or otherwise transfer your personal information to third parties without your 
              consent, except in the following circumstances:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>With your explicit consent</PrivacyListItem>
              <PrivacyListItem>To comply with legal obligations</PrivacyListItem>
              <PrivacyListItem>To protect our rights and safety</PrivacyListItem>
              <PrivacyListItem>In connection with a business transfer or merger</PrivacyListItem>
            </PrivacyList>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>4. Data Security</PrivacySubtitle>
            <PrivacyText>
              We implement appropriate security measures to protect your personal information against unauthorized 
              access, alteration, disclosure, or destruction. These measures include:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>Encryption of data in transit and at rest</PrivacyListItem>
              <PrivacyListItem>Regular security assessments and updates</PrivacyListItem>
              <PrivacyListItem>Access controls and authentication</PrivacyListItem>
              <PrivacyListItem>Employee training on data protection</PrivacyListItem>
            </PrivacyList>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>5. Data Retention</PrivacySubtitle>
            <PrivacyText>
              We retain your personal information for as long as necessary to provide our services and fulfill 
              the purposes outlined in this policy. You may request deletion of your account and associated data 
              at any time through your account settings.
            </PrivacyText>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>6. Your Rights</PrivacySubtitle>
            <PrivacyText>
              You have the following rights regarding your personal information:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>Access and review your personal information</PrivacyListItem>
              <PrivacyListItem>Update or correct inaccurate information</PrivacyListItem>
              <PrivacyListItem>Request deletion of your account and data</PrivacyListItem>
              <PrivacyListItem>Opt out of certain communications</PrivacyListItem>
              <PrivacyListItem>Export your data in a portable format</PrivacyListItem>
            </PrivacyList>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>7. Cookies and Tracking</PrivacySubtitle>
            <PrivacyText>
              We use cookies and similar technologies to enhance your experience, analyze usage, and provide 
              personalized content. You can control cookie settings through your browser preferences.
            </PrivacyText>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>8. Third-Party Services</PrivacySubtitle>
            <PrivacyText>
              Our service may contain links to third-party websites or integrate with third-party services. 
              We are not responsible for the privacy practices of these third parties. We encourage you to 
              review their privacy policies.
            </PrivacyText>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>9. Children's Privacy</PrivacySubtitle>
            <PrivacyText>
              Our service is not intended for children under 13 years of age. We do not knowingly collect 
              personal information from children under 13. If you believe we have collected information from 
              a child under 13, please contact us immediately.
            </PrivacyText>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>10. International Data Transfers</PrivacySubtitle>
            <PrivacyText>
              Your information may be transferred to and processed in countries other than your own. We ensure 
              that such transfers comply with applicable data protection laws and implement appropriate safeguards.
            </PrivacyText>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>11. Changes to This Policy</PrivacySubtitle>
            <PrivacyText>
              We may update this privacy policy from time to time. We will notify you of any material changes 
              by posting the new policy on this page and updating the "Last Updated" date.
            </PrivacyText>
          </PrivacySection>

          <PrivacySection>
            <PrivacySubtitle>12. Contact Us</PrivacySubtitle>
            <PrivacyText>
              If you have any questions about this privacy policy or our data practices, please contact us at:
            </PrivacyText>
            <PrivacyList>
              <PrivacyListItem>Email: privacy@weloop.tech</PrivacyListItem>
              <PrivacyListItem>Address: 123 Privacy Street, Security City, SC 12345</PrivacyListItem>
            </PrivacyList>
          </PrivacySection>
        </PrivacyContainer>

        <LastUpdated>
          Last Updated: January 15, 2024
        </LastUpdated>
      </Content>
    </Container>
  );
};

export default PrivacyPage; 